import { useMemo } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as R from 'ramda'

import { OnboardingAPI } from 'api'
import { bulkUpdateTeams, updateSalesRepName } from 'api/customer'
import { trackEvent } from 'utils/tracking'

interface SalesRepSetupData {
  selectedTeamIds?: number[]
  selectedRoleNames?: string[]
}

export const useSalesSetupData = ({
  selectedTeamIds = [],
  selectedRoleNames = [],
}: SalesRepSetupData) => {
  const queryClient = useQueryClient()

  const {
    data = [],
    isError,
    isLoading,
  } = useQuery({
    queryKey: ['salesRepSetupData', selectedTeamIds],
    queryFn: () => {
      if (selectedTeamIds.length) {
        const params = selectedTeamIds.map((id) => `teamId=${id}`).join('&')
        return OnboardingAPI.get(`/users/setup?${params}`).then(
          ({ data }) => data
        )
      } else {
        return OnboardingAPI.get('/users/setup').then(({ data }) => data)
      }
    },
    staleTime: 60000,
  })

  const saveTeamData = useMutation({
    mutationFn: bulkUpdateTeams,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['salesRepSetupData', selectedTeamIds],
      })
    },
  })

  const saveNameData = useMutation({
    mutationFn: updateSalesRepName,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['salesRepSetupData', selectedTeamIds],
      })
    },
  })

  const saveSalesRepData = async ({
    teamChanged,
    teamData,
    userData,
    nameChanged,
    onSuccess,
    onError,
  }) => {
    try {
      if (teamChanged) {
        trackEvent('SALES_REP_TEAM_EDITED', {
          teamData: teamData,
        })
        await saveTeamData.mutateAsync(teamData)
      }
      if (nameChanged) {
        trackEvent('SALES_REP_NAME_EDITED', {
          userData: userData,
        })
        await saveNameData.mutateAsync(userData)
      }

      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      if (onError) {
        onError(error)
      }
    }
  }

  const roles = R.pipe(R.pluck('roleNames'), R.flatten, R.uniq)(data)

  const salesSetup = useMemo(() => {
    if (selectedRoleNames.length) {
      return data.filter((item) => {
        const { roleNames = [] } = item

        return roleNames.some((role) => selectedRoleNames.includes(role))
      })
    } else {
      return data
    }
  }, [selectedRoleNames, data])

  return {
    roles,
    salesSetup,
    isError,
    isLoading,
    saveSalesRepData,
    isSavingTeam: saveTeamData.isLoading,
    isSavingName: saveNameData.isLoading,
  }
}
